import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useTranslation, useLanguage, useSettings, parseApiError } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Palette, Globe, Loader2, Check } from 'lucide-react';

const SettingsPage = () => {
  const t = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const { updateSettings: updateGlobalSettings } = useSettings();
  const [settings, setSettings] = useState({
    primary_color: '#C8102E',
    dark_color: '#A50E27',
    soft_color: '#F8D7DA',
    language: 'en'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      changeLanguage(settings.language);
      // Update global settings to apply colors immediately
      updateGlobalSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const colorPresets = [
    { name: 'Tel-U Red', primary: '#C8102E', dark: '#A50E27', soft: '#F8D7DA' },
    { name: 'Ocean Blue', primary: '#0066CC', dark: '#004C99', soft: '#CCE0FF' },
    { name: 'Forest Green', primary: '#228B22', dark: '#1A6B1A', soft: '#D4EDDA' },
    { name: 'Royal Purple', primary: '#6B3FA0', dark: '#512D7D', soft: '#E5D5F5' },
    { name: 'Sunset Orange', primary: '#FF6B35', dark: '#E55A2B', soft: '#FFE4D6' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
          {t('brandSettings')}
        </h1>
        <p className="text-gray-500 mt-1">Customize the look and feel of your application</p>
      </div>

      {/* Color Settings */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
            <Palette size={20} />
            Brand Colors
          </CardTitle>
          <CardDescription>
            Choose colors that represent your brand
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Color Presets */}
          <div className="space-y-3">
            <Label>Quick Presets</Label>
            <div className="grid grid-cols-5 gap-2">
              {colorPresets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setSettings({
                    ...settings,
                    primary_color: preset.primary,
                    dark_color: preset.dark,
                    soft_color: preset.soft
                  })}
                  className={`relative p-3 rounded-lg border-2 transition-all ${
                    settings.primary_color === preset.primary 
                      ? 'border-gray-900 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div 
                    className="w-full h-8 rounded"
                    style={{ backgroundColor: preset.primary }}
                  />
                  <span className="text-xs text-gray-600 mt-1 block truncate">
                    {preset.name}
                  </span>
                  {settings.primary_color === preset.primary && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('primaryColor')}</Label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-10 h-10 rounded border border-gray-200"
                  style={{ backgroundColor: settings.primary_color }}
                />
                <Input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  placeholder="#C8102E"
                  className="font-mono"
                  data-testid="primary-color-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dark Color (Hover)</Label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-10 h-10 rounded border border-gray-200"
                  style={{ backgroundColor: settings.dark_color }}
                />
                <Input
                  type="text"
                  value={settings.dark_color}
                  onChange={(e) => setSettings({ ...settings, dark_color: e.target.value })}
                  placeholder="#A50E27"
                  className="font-mono"
                  data-testid="dark-color-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Soft Color (Background)</Label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-10 h-10 rounded border border-gray-200"
                  style={{ backgroundColor: settings.soft_color }}
                />
                <Input
                  type="text"
                  value={settings.soft_color}
                  onChange={(e) => setSettings({ ...settings, soft_color: e.target.value })}
                  placeholder="#F8D7DA"
                  className="font-mono"
                  data-testid="soft-color-input"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3">
              <div className="flex items-center gap-3">
                <button 
                  className="px-4 py-2 rounded-md text-white text-sm font-medium transition-all"
                  style={{ backgroundColor: settings.primary_color }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = settings.dark_color}
                  onMouseLeave={(e) => e.target.style.backgroundColor = settings.primary_color}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded-md text-sm font-medium border"
                  style={{ 
                    borderColor: settings.primary_color, 
                    color: settings.primary_color 
                  }}
                >
                  Secondary Button
                </button>
              </div>
              <div 
                className="p-3 rounded-md"
                style={{ backgroundColor: settings.soft_color }}
              >
                <p className="text-sm" style={{ color: settings.dark_color }}>
                  This is an alert or notification area using soft background color.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
            <Globe size={20} />
            {t('language')}
          </CardTitle>
          <CardDescription>
            Choose your preferred language
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>{t('language')}</Label>
            <Select 
              value={settings.language} 
              onValueChange={(v) => setSettings({ ...settings, language: v })}
            >
              <SelectTrigger className="w-full max-w-xs" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="id">Bahasa Indonesia</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {t('save')} {t('settings')}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
